// Phase 11 — Tasks route.
import * as React from 'react';
import { useTasks } from '../../hooks/useTasks.js';
import { Card, CardContent } from '../../components/ui/Card.js';
import { SkeletonRow } from '../../components/ui/Skeleton.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { Button } from '../../components/ui/Button.js';

const PRIORITY_COLORS: Record<string, string> = { high: 'text-state-danger', medium: 'text-state-warning', low: 'text-text-muted' };
const STATUS_LABELS: Record<string, string> = { todo: 'To do', in_progress: 'In progress', done: 'Done' };

export function Tasks(): JSX.Element {
  React.useEffect(() => { document.title = 'DailyDeck — Tasks'; }, []);
  const query = useTasks();

  return (
    <div className="space-y-6" data-testid="tasks-route">
      <header>
        <h1 className="text-title-1 font-semibold text-text-primary">Tasks</h1>
        <p className="text-body text-text-secondary mt-1">Task management with priorities and due dates.</p>
      </header>

      {query.isPending && <div className="rounded-lg border border-border-subtle bg-surface-raised"><SkeletonRow /><SkeletonRow /><SkeletonRow /></div>}
      {query.isError && <Card><CardContent className="py-4"><p className="text-body-strong text-text-primary">Could not load tasks</p><Button variant="secondary" className="mt-3" onClick={() => void query.refetch()}>Retry</Button></CardContent></Card>}
      {query.isSuccess && query.data.length === 0 && <EmptyState title="No tasks yet" description="Add a task to get started." />}
      {query.isSuccess && query.data.length > 0 && (
        <div className="space-y-2">
          {query.data.map((t) => (
            <Card key={t.id}>
              <CardContent className="flex items-start justify-between gap-4 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {t.status === 'done' ? <s className="text-body-strong text-text-muted">{t.title}</s> : <span className="text-body-strong font-medium text-text-primary">{t.title}</span>}
                    <span className={`text-micro font-semibold uppercase ${PRIORITY_COLORS[t.priority] ?? ''}`}>{t.priority}</span>
                  </div>
                  {t.description && <p className="text-caption text-text-secondary mt-0.5 line-clamp-1">{t.description}</p>}
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="text-micro text-text-muted">{STATUS_LABELS[t.status] ?? t.status}</span>
                    {t.project && <span className="text-micro text-accent-default bg-accent-subtle rounded px-1.5 py-0.5">{t.project}</span>}
                    {t.due_date && <span className="text-micro text-text-muted tabular-nums">Due: {t.due_date}</span>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
